import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/ui/Toast';
import { Button, Input, Card, DataTable, Modal } from '../../components/ui';
import { ConfirmModal } from '../../components/ui/Modal';

// Mappatura dettagliata dei tipi documento con informazioni su utilizzo e collegamento
const TIPI_DOCUMENTO_INFO = {
  // Documenti Contrattuali (100000000-100000011)
  100000000: { ambito: 'Contratti', descrizione: 'Contratto firmato tra RSA e Fornitore', icon: '📝' },
  100000001: { ambito: 'Contratti', descrizione: 'Appendice o modifica contrattuale', icon: '📎' },
  100000002: { ambito: 'Contratti', descrizione: 'Service Level Agreement', icon: '📊' },
  100000003: { ambito: 'Interventi', descrizione: 'Rapportino di manutenzione eseguita', icon: '🔧' },
  100000004: { ambito: 'Interventi', descrizione: 'Verbale di sopralluogo tecnico', icon: '📋' },
  100000005: { ambito: 'Fornitori', descrizione: 'Certificazioni del fornitore', icon: '✅' },
  100000006: { ambito: 'Fornitori', descrizione: 'Polizza assicurativa fornitore', icon: '🛡️' },
  100000007: { ambito: 'Fornitori', descrizione: 'Documento Unico Regolarità Contributiva', icon: '📄' },
  100000008: { ambito: 'Fornitori', descrizione: 'Visura camerale fornitore', icon: '🏛️' },
  100000009: { ambito: 'Contratti', descrizione: 'Documentazione gara/appalto', icon: '📁' },
  100000010: { ambito: 'Contratti', descrizione: 'Fatture e documenti contabili', icon: '💰' },
  100000011: { ambito: 'Generale', descrizione: 'Altri documenti non categorizzati', icon: '📄' },
  // Documenti Immobili (100000012-100000015)
  100000012: { ambito: 'Immobili', descrizione: 'Planimetrie e mappe edificio', icon: '📐' },
  100000013: { ambito: 'Immobili', descrizione: 'Scheda tecnica immobile/impianto', icon: '📋' },
  100000014: { ambito: 'Immobili', descrizione: 'DICO - Dichiarazione di conformità impianti', icon: '✅' },
  100000015: { ambito: 'Immobili', descrizione: 'Documentazione tecnica impianti', icon: '⚙️' }
};

// Colori per ambito
const AMBITO_COLORS = {
  'Contratti': { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
  'Interventi': { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200' },
  'Fornitori': { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200' },
  'Immobili': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
  'Generale': { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200' },
  'Personalizzato': { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-200' }
};

// Range per tipi personalizzati per ambito
const AMBITO_RANGES = {
  'Contratti': { start: 100000100, end: 100000199 },
  'Interventi': { start: 100000200, end: 100000299 },
  'Fornitori': { start: 100000300, end: 100000399 },
  'Immobili': { start: 100000400, end: 100000499 },
  'Generale': { start: 100000500, end: 100000599 }
};

// Lista ambiti disponibili per creazione
const AMBITI_DISPONIBILI = [
  { value: 'Contratti', label: 'Contratti', icon: '📝', description: 'Documenti contrattuali' },
  { value: 'Interventi', label: 'Interventi', icon: '🔧', description: 'Rapportini e verbali' },
  { value: 'Fornitori', label: 'Fornitori', icon: '🏢', description: 'Certificazioni e documenti fornitore' },
  { value: 'Immobili', label: 'Immobili', icon: '🏠', description: 'Documenti tecnici immobili' },
  { value: 'Generale', label: 'Generale', icon: '📄', description: 'Altri documenti' }
];

const getTipoInfo = (value) => {
  // Prima controlla i tipi predefiniti
  if (TIPI_DOCUMENTO_INFO[value]) {
    return TIPI_DOCUMENTO_INFO[value];
  }

  // Poi controlla i range personalizzati
  for (const [ambito, range] of Object.entries(AMBITO_RANGES)) {
    if (value >= range.start && value <= range.end) {
      return {
        ambito: ambito,
        descrizione: `Tipo documento personalizzato (${ambito})`,
        icon: AMBITI_DISPONIBILI.find(a => a.value === ambito)?.icon || '✨'
      };
    }
  }

  // Fallback per tipi vecchi (> 100000015 ma < 100000100)
  return {
    ambito: 'Personalizzato',
    descrizione: 'Tipo documento personalizzato',
    icon: '✨'
  };
};

const getAmbitoColor = (ambito) => {
  return AMBITO_COLORS[ambito] || AMBITO_COLORS['Personalizzato'];
};

// Helper per ottenere categoria semplificata (per UI)
const getCategoria = (value) => {
  if (value >= 100000000 && value <= 100000011) {
    return { key: 'contratti', label: 'Documenti Contrattuali' };
  } else if (value >= 100000012 && value <= 100000015) {
    return { key: 'immobili', label: 'Documenti Immobili' };
  }
  return { key: 'altro', label: 'Personalizzato' };
};

const getCategoriaColor = (key) => {
  const colors = {
    'contratti': 'bg-blue-100 text-blue-800',
    'immobili': 'bg-green-100 text-green-800',
    'altro': 'bg-purple-100 text-purple-800'
  };
  return colors[key] || colors['altro'];
};

function TipiDocumentoPage() {
  const { apiRequest } = useAuth();
  const { showToast } = useToast();

  const [tipiDocumento, setTipiDocumento] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nextValue, setNextValue] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    label: '',
    ambito: 'Generale'
  });

  // Load tipi documento
  useEffect(() => {
    loadTipiDocumento();
  }, []);

  const loadTipiDocumento = async () => {
    setLoading(true);
    try {
      const [optionsData, nextValueData] = await Promise.all([
        apiRequest('/admin/dataverse/tipodocumento-options'),
        apiRequest('/admin/dataverse/tipodocumento-options/next-value')
      ]);
      setTipiDocumento(optionsData.options || []);
      setNextValue(nextValueData.nextValue);
    } catch (error) {
      console.error('Error loading tipi documento:', error);
      showToast('Errore nel caricamento dei tipi documento', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingItem(null);
    setFormData({ label: '', ambito: 'Generale' });
    setModalOpen(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    const info = getTipoInfo(item.value);
    setFormData({ label: item.label || '', ambito: info.ambito });
    setModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.label.trim()) {
      showToast('Il nome del tipo documento è obbligatorio', 'error');
      return;
    }

    setSaving(true);
    try {
      if (editingItem) {
        // Update - includi ambito per tipi non di sistema
        const isSystemType = editingItem.value >= 100000000 && editingItem.value <= 100000015;
        const currentInfo = getTipoInfo(editingItem.value);
        const ambitoChanged = !isSystemType && formData.ambito !== currentInfo.ambito;

        const updateData = { label: formData.label.trim() };
        if (ambitoChanged) {
          updateData.ambito = formData.ambito;
        }

        const result = await apiRequest(`/admin/dataverse/tipodocumento-options/${editingItem.value}`, {
          method: 'PUT',
          body: JSON.stringify(updateData)
        });

        if (result.migrated) {
          showToast(`Tipo documento migrato con successo a "${formData.ambito}"`, 'success');
        } else {
          showToast('Tipo documento aggiornato con successo', 'success');
        }
      } else {
        // Create - il backend assegnerà il valore in base all'ambito
        await apiRequest('/admin/dataverse/tipodocumento-options', {
          method: 'POST',
          body: JSON.stringify({
            label: formData.label.trim(),
            ambito: formData.ambito
          })
        });
        showToast('Tipo documento creato con successo', 'success');
      }

      setModalOpen(false);
      loadTipiDocumento();
    } catch (error) {
      console.error('Error saving tipo documento:', error);
      if (error.message?.includes('Conflict') || error.message?.includes('already exists')) {
        showToast('Questo tipo documento esiste già', 'error');
      } else {
        showToast(error.message || 'Errore durante il salvataggio', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      await apiRequest(`/admin/dataverse/tipodocumento-options/${itemToDelete.value}`, {
        method: 'DELETE'
      });
      showToast('Tipo documento eliminato con successo', 'success');
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      loadTipiDocumento();
    } catch (error) {
      console.error('Error deleting tipo documento:', error);
      if (error.message?.includes('Conflict') || error.message?.includes('documents using')) {
        showToast('Impossibile eliminare: ci sono documenti che usano questo tipo', 'error');
      } else {
        showToast(error.message || 'Errore durante l\'eliminazione', 'error');
      }
    }
  };

  const confirmDelete = (item) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  // Stats per ambito (entità collegata)
  const statsByAmbito = tipiDocumento.reduce((acc, t) => {
    const info = getTipoInfo(t.value);
    acc[info.ambito] = (acc[info.ambito] || 0) + 1;
    return acc;
  }, {});

  // Stats per categoria sistema
  const stats = {
    totale: tipiDocumento.length,
    contratti: tipiDocumento.filter(t => t.value >= 100000000 && t.value <= 100000011).length,
    immobili: tipiDocumento.filter(t => t.value >= 100000012 && t.value <= 100000015).length,
    personalizzati: tipiDocumento.filter(t => t.value > 100000015).length
  };

  // Columns - più user friendly
  const columns = [
    {
      key: 'label',
      label: 'Nome Tipo Documento',
      sortable: true,
      render: (row) => {
        const info = getTipoInfo(row.value);
        return (
          <div className="flex items-center gap-3">
            <span className="text-xl">{info.icon}</span>
            <div>
              <div className="font-medium text-gray-900">{row.label}</div>
              <div className="text-xs text-gray-500">{info.descrizione}</div>
            </div>
          </div>
        );
      }
    },
    {
      key: 'ambito',
      label: 'Collegato a',
      render: (row) => {
        const info = getTipoInfo(row.value);
        const colors = getAmbitoColor(info.ambito);
        return (
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}>
            {info.ambito}
          </span>
        );
      }
    },
    {
      key: 'categoria',
      label: 'Categoria Sistema',
      render: (row) => {
        const cat = getCategoria(row.value);
        return (
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoriaColor(cat.key)}`}>
            {cat.label}
          </span>
        );
      }
    },
    {
      key: 'actions',
      label: 'Azioni',
      render: (row) => {
        const cat = getCategoria(row.value);
        const isSystemType = cat.key !== 'altro'; // I tipi di sistema (contratti e immobili) hanno restrizioni

        return (
          <div className="flex gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); handleOpenEdit(row); }}
              className="p-2 text-primary-600 hover:text-primary-800 hover:bg-primary-50 rounded"
              title="Modifica nome"
            >
              ✏️
            </button>
            {!isSystemType && (
              <button
                onClick={(e) => { e.stopPropagation(); confirmDelete(row); }}
                className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                title="Elimina"
              >
                🗑️
              </button>
            )}
            {isSystemType && (
              <span className="p-2 text-gray-300 cursor-not-allowed" title="Tipo di sistema - non eliminabile">
                🔒
              </span>
            )}
          </div>
        );
      }
    }
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <a href="/admin" className="hover:text-primary-600">Amministrazione</a>
            <span>/</span>
            <span>Tipi Documento</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Tipi Documento</h1>
          <p className="text-gray-500 mt-1">
            Gestisci le categorie di documenti disponibili nel portale
          </p>
        </div>

        <Button variant="primary" onClick={handleOpenCreate}>
          + Nuovo Tipo Documento
        </Button>
      </div>

      {/* Stats per Ambito */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Tipi documento per entità collegata</h3>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-gray-100 rounded">
                <span className="text-lg">📋</span>
              </div>
              <div>
                <div className="text-xl font-bold text-gray-900">{stats.totale}</div>
                <div className="text-xs text-gray-500">Totale</div>
              </div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-blue-100 rounded">
                <span className="text-lg">📝</span>
              </div>
              <div>
                <div className="text-xl font-bold text-blue-600">{statsByAmbito['Contratti'] || 0}</div>
                <div className="text-xs text-gray-500">Contratti</div>
              </div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-orange-100 rounded">
                <span className="text-lg">🔧</span>
              </div>
              <div>
                <div className="text-xl font-bold text-orange-600">{statsByAmbito['Interventi'] || 0}</div>
                <div className="text-xs text-gray-500">Interventi</div>
              </div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-purple-100 rounded">
                <span className="text-lg">🏢</span>
              </div>
              <div>
                <div className="text-xl font-bold text-purple-600">{statsByAmbito['Fornitori'] || 0}</div>
                <div className="text-xs text-gray-500">Fornitori</div>
              </div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-green-100 rounded">
                <span className="text-lg">🏠</span>
              </div>
              <div>
                <div className="text-xl font-bold text-green-600">{statsByAmbito['Immobili'] || 0}</div>
                <div className="text-xs text-gray-500">Immobili</div>
              </div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-indigo-100 rounded">
                <span className="text-lg">✨</span>
              </div>
              <div>
                <div className="text-xl font-bold text-indigo-600">{statsByAmbito['Personalizzato'] || 0}</div>
                <div className="text-xs text-gray-500">Personalizzati</div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <span className="text-xl">💡</span>
          <div className="text-sm text-blue-800 space-y-2">
            <p>
              <strong>Ogni tipo documento è collegato a un'entità specifica</strong> del portale:
            </p>
            <ul className="list-disc list-inside ml-2 space-y-1">
              <li><span className="text-blue-600 font-medium">Contratti</span> → documenti visibili nella sezione Contratti Fornitore</li>
              <li><span className="text-orange-600 font-medium">Interventi</span> → rapportini e verbali degli interventi</li>
              <li><span className="text-purple-600 font-medium">Fornitori</span> → certificazioni, DURC, visure dei fornitori</li>
              <li><span className="text-green-600 font-medium">Immobili</span> → planimetrie, schede tecniche, DICO degli immobili</li>
              <li><span className="text-gray-600 font-medium">Generale</span> → documenti non categorizzati</li>
            </ul>
            <p className="pt-1 border-t border-blue-200 mt-2">
              I tipi predefiniti di sistema possono essere rinominati ma non eliminati. I tipi <strong>Personalizzati</strong> sono liberamente modificabili.
            </p>
          </div>
        </div>
      </div>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        <DataTable
          data={tipiDocumento}
          columns={columns}
          loading={loading}
          searchable
          searchPlaceholder="Cerca tipo documento..."
          emptyMessage="Nessun tipo documento trovato"
          onRowClick={handleOpenEdit}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingItem ? 'Modifica Tipo Documento' : 'Nuovo Tipo Documento'}
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Nome del tipo documento *"
            name="label"
            value={formData.label}
            onChange={handleChange}
            placeholder="es. Certificato energetico, Relazione tecnica..."
            maxLength={200}
            autoFocus
          />

          {/* Selettore Ambito - mostrato sempre, ma disabilitato per tipi di sistema */}
          {(() => {
            const isSystemType = editingItem && editingItem.value >= 100000000 && editingItem.value <= 100000015;
            const currentInfo = editingItem ? getTipoInfo(editingItem.value) : null;

            return (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Collegato a (entità) {!editingItem && '*'}
                </label>

                {isSystemType ? (
                  // Tipo di sistema - mostra solo info, non modificabile
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{currentInfo?.icon}</span>
                      <div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getAmbitoColor(currentInfo?.ambito).bg} ${getAmbitoColor(currentInfo?.ambito).text}`}>
                          {currentInfo?.ambito}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          🔒 Tipo di sistema - l'ambito non può essere modificato
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Tipo personalizzato o nuovo - selezione ambito
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      {AMBITI_DISPONIBILI.map(amb => {
                        const isSelected = formData.ambito === amb.value;
                        const colors = getAmbitoColor(amb.value);
                        return (
                          <button
                            key={amb.value}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, ambito: amb.value }))}
                            className={`p-3 rounded-lg border-2 text-left transition-all ${
                              isSelected
                                ? `${colors.bg} ${colors.border} ring-2 ring-offset-1`
                                : 'border-gray-200 hover:border-gray-300 bg-white'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{amb.icon}</span>
                              <div>
                                <div className={`font-medium ${isSelected ? colors.text : 'text-gray-900'}`}>
                                  {amb.label}
                                </div>
                                <div className="text-xs text-gray-500">{amb.description}</div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {editingItem && currentInfo && formData.ambito !== currentInfo.ambito && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                        <div className="flex items-start gap-2">
                          <span>⚠️</span>
                          <span>
                            Cambiare l'ambito è possibile solo se non ci sono documenti associati a questo tipo.
                            Il tipo verrà migrato da <strong>{currentInfo.ambito}</strong> a <strong>{formData.ambito}</strong>.
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="ghost" onClick={() => setModalOpen(false)}>
              Annulla
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={saving || !formData.label.trim()}
            >
              {saving ? 'Salvataggio...' : (editingItem ? 'Salva' : 'Crea Tipo Documento')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => { setDeleteConfirmOpen(false); setItemToDelete(null); }}
        onConfirm={handleDelete}
        title="Elimina Tipo Documento"
        message={
          <>
            Sei sicuro di voler eliminare il tipo documento <strong>"{itemToDelete?.label}"</strong>?
            <br /><br />
            <span className="text-amber-600">
              ⚠️ Se ci sono documenti che usano questo tipo, l'eliminazione non sarà possibile.
            </span>
          </>
        }
        confirmLabel="Elimina"
        variant="danger"
      />
    </div>
  );
}

export default TipiDocumentoPage;
